import React, {
  useState,
  useMemo,
  useCallback,
} from 'react';

import { useAuth } from '../context/AuthContext';
import { useApiQuery } from '../hooks/useApiQuery';
import api from '../services/api';

import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import {
  CalendarIcon,
  UserGroupIcon,
  QrCodeIcon,
  FireIcon,
  ClockIcon,
  ChartBarIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';


const Dashboard = () => {
  const { user } = useAuth();

  const [activeTab, setActiveTab] =
    useState('overview');


  // =========================================================
  // DASHBOARD STATS
  //
  // Needed for:
  // - Overview
  //
  // Do NOT fetch this for analytics-only tabs.
  // =========================================================

  const {
    data: statsData = {},
    isLoading: statsLoading,
    isError: statsError,
    refetch: refetchStats,
  } = useApiQuery(
    ['dashboard-stats'],
    () =>
      api
        .get('/dashboard/stats')
        .then(res => res.data),
    {
      enabled:
        activeTab === 'overview',

      staleTime:
        60 * 1000,
    }
  );


  // =========================================================
  // EVENTS
  //
  // Needed for:
  // - Overview
  // - Event Analytics
  //
  // =========================================================

  const {
    data: eventsResponse = [],
    isLoading: eventsLoading,
    isError: eventsError,
    refetch: refetchEvents,
  } = useApiQuery(
    ['all-events'],
    () =>
      api
        .get('/events')
        .then(res => res.data),
    {
      enabled:
        activeTab === 'overview' ||
        activeTab === 'event-analytics',

      staleTime:
        5 * 60 * 1000,
    }
  );


  // =========================================================
  // SESSION ANALYTICS
  //
  // Needed for:
  // - Overview
  // - Session Analytics
  // - Device Analytics
  //
  // IMPORTANT:
  // We DO NOT fetch /sessions.
  //
  // Backend returns aggregated analytics.
  // =========================================================

  const {
    data: sessionAnalyticsData = {},
    isLoading: sessionAnalyticsLoading,
    isError: sessionAnalyticsError,
    refetch: refetchSessionAnalytics,
  } = useApiQuery(
    ['session-analytics'],
    () =>
      api
        .get('/sessions/analytics')
        .then(res => res.data),
    {
      enabled:
        activeTab === 'overview' ||
        activeTab === 'session-analytics' ||
        activeTab === 'device-analytics',

      staleTime:
        60 * 1000,
    }
  );


  // =========================================================
  // IMPORTANT
  //
  // REMOVED:
  //
  // const { data: devices } = useApiQuery(...)
  //
  // Because device analytics already comes from:
  //
  // /sessions/analytics
  //
  // Therefore /devices was an unnecessary Dashboard request.
  // =========================================================


  // =========================================================
  // LOADING STATE
  // =========================================================

  const isLoading =
    (activeTab === 'overview' &&
      (
        statsLoading ||
        eventsLoading ||
        sessionAnalyticsLoading
      )) ||

    (activeTab === 'event-analytics' &&
      eventsLoading) ||

    (activeTab === 'session-analytics' &&
      sessionAnalyticsLoading) ||

    (activeTab === 'device-analytics' &&
      sessionAnalyticsLoading);


  // =========================================================
  // ERROR STATE
  // =========================================================

  const hasError =
    (activeTab === 'overview' &&
      (
        statsError ||
        eventsError ||
        sessionAnalyticsError
      )) ||

    (activeTab === 'event-analytics' &&
      eventsError) ||

    (activeTab === 'session-analytics' &&
      sessionAnalyticsError) ||

    (activeTab === 'device-analytics' &&
      sessionAnalyticsError);


  // =========================================================
  // NORMALIZE EVENTS
  //
  // Supports both:
  //
  // [...]
  //
  // and:
  //
  // { data: [...] }
  // =========================================================

  const events = useMemo(() => {

    if (Array.isArray(eventsResponse)) {
      return eventsResponse;
    }

    if (
      Array.isArray(
        eventsResponse?.data
      )
    ) {
      return eventsResponse.data;
    }

    return [];

  }, [eventsResponse]);


  // =========================================================
  // OVERVIEW
  // =========================================================

  const overview = useMemo(() => {

    const totalEvents =
      events.length;


    const totalUsers =
      Number(
        statsData?.totalUsers
      ) || 0;


    const totalSessions =
      Number(
        sessionAnalyticsData?.totalSessions
      ) || 0;


    const totalScans =
      totalSessions;


    const averageAttendance =
      totalEvents > 0
        ? events.reduce(
            (sum, event) =>
              sum +
              (
                event?.users?.length || 0
              ),
            0
          ) / totalEvents
        : 0;


    return {
      totalEvents,
      totalUsers,
      totalScans,
      totalSessions,
      averageAttendance,
    };

  }, [
    events,
    statsData,
    sessionAnalyticsData,
  ]);


  // =========================================================
  // EVENT COMPARISON
  // =========================================================

  const eventComparison =
    useMemo(() => {

      return events.map(event => ({
        name:
          event?.name ||
          'Unnamed Event',

        users:
          event?.users?.length ||
          0,

        scans:
          Number(
            event?.totalScans
          ) || 0,
      }));

    }, [events]);


  // =========================================================
  // ANALYTICS DATA
  // =========================================================

  const attendanceTrend =
    Array.isArray(
      sessionAnalyticsData?.attendanceTrend
    )
      ? sessionAnalyticsData.attendanceTrend
      : [];


  const peakHours =
    Array.isArray(
      sessionAnalyticsData?.peakHours
    )
      ? sessionAnalyticsData.peakHours
      : [];


  const deviceAnalytics =
    Array.isArray(
      sessionAnalyticsData?.deviceAnalytics
    )
      ? sessionAnalyticsData.deviceAnalytics
      : [];


  // =========================================================
  // REFRESH
  //
  // Refresh only the data required
  // by the currently selected tab.
  // =========================================================

  const handleRefresh =
    useCallback(() => {

      if (
        activeTab === 'overview'
      ) {
        refetchStats();
        refetchEvents();
        refetchSessionAnalytics();
        return;
      }


      if (
        activeTab === 'event-analytics'
      ) {
        refetchEvents();
        return;
      }


      if (
        activeTab === 'session-analytics' ||
        activeTab === 'device-analytics'
      ) {
        refetchSessionAnalytics();
      }

    }, [
      activeTab,
      refetchStats,
      refetchEvents,
      refetchSessionAnalytics,
    ]);


  // =========================================================
  // LOADING UI
  // =========================================================

  if (isLoading) {

    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">

        <div className="text-center">

          <div
            className="
              inline-block
              w-10
              h-10
              border-2
              border-[#1672b9]
              border-t-transparent
              rounded-full
              animate-spin
              mb-3
            "
          />

          <p className="text-gray-500 text-sm">
            Loading dashboard...
          </p>

        </div>

      </div>
    );
  }


  // =========================================================
  // ERROR UI
  // =========================================================

  if (hasError) {

    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">

        <div className="text-center">

          <p className="text-red-500 text-sm font-medium">
            Failed to load dashboard data
          </p>

          <p className="text-gray-500 text-xs mt-2">
            Please refresh the page or try again later.
          </p>

          <button
            onClick={handleRefresh}
            className="
              inline-flex
              items-center
              gap-2
              mt-4
              px-4
              py-2
              text-sm
              text-white
              bg-[#1672b9]
              rounded-lg
              hover:bg-[#0f5a9e]
              transition
            "
          >
            <ArrowPathIcon className="w-4 h-4" />
            Retry
          </button>

        </div>

      </div>
    );
  }


  // =========================================================
  // TOOLTIP
  // =========================================================

  const tooltipStyle = {
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow:
      '0 1px 2px rgba(0,0,0,0.05)',
    fontSize: '12px',
    color: '#111827',
    padding: '8px 12px',
  };


  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div className="space-y-6">


      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="flex flex-wrap items-center justify-between gap-4">

        <div>

          <h1 className="text-2xl font-bold text-gray-900">
            Dashboard
          </h1>

          <p className="text-sm text-gray-500 mt-1">
            Welcome back, {user?.name} – here's what's happening
            with your events
          </p>

        </div>


        <button
          onClick={handleRefresh}
          className="
            inline-flex
            items-center
            gap-2
            px-3
            py-2
            text-sm
            text-gray-600
            bg-white
            border
            border-gray-200
            rounded-lg
            hover:bg-gray-50
            hover:text-[#1672b9]
            transition-all
          "
        >

          <ArrowPathIcon className="w-4 h-4" />

          Refresh

        </button>

      </div>


      {/* =====================================================
          TABS
      ===================================================== */}

      <div className="border-b border-gray-200">

        <nav className="flex flex-wrap gap-1">

          {[
            'overview',
            'event-analytics',
            'session-analytics',
            'device-analytics',
          ].map(tab => (

            <button
              key={tab}
              onClick={() =>
                setActiveTab(tab)
              }
              className={`
                px-5
                py-2.5
                text-sm
                font-medium
                rounded-t-lg
                transition-all

                ${
                  activeTab === tab
                    ? 'text-[#1672b9] border-b-2 border-[#1672b9] bg-white'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }
              `}
            >

              {tab
                .charAt(0)
                .toUpperCase() +
                tab
                  .slice(1)
                  .replace(
                    '-',
                    ' '
                  )}

            </button>

          ))}

        </nav>

      </div>


      {/* =====================================================
          OVERVIEW TAB
      ===================================================== */}

      {activeTab === 'overview' && (

        <div className="space-y-6">


          {/* Metric Cards */}

          <div className="
            grid
            grid-cols-1
            sm:grid-cols-2
            lg:grid-cols-3
            xl:grid-cols-5
            gap-5
          ">

            <MetricCard
              title="Total Events"
              value={
                overview.totalEvents
              }
              icon={
                <CalendarIcon
                  className="w-5 h-5 text-[#1672b9]"
                />
              }
            />


            <MetricCard
              title="Total Users"
              value={
                overview.totalUsers
              }
              icon={
                <UserGroupIcon
                  className="w-5 h-5 text-[#1672b9]"
                />
              }
            />


            <MetricCard
              title="Total Scans"
              value={
                overview.totalScans
              }
              icon={
                <QrCodeIcon
                  className="w-5 h-5 text-[#1672b9]"
                />
              }
            />


            <MetricCard
              title="Total Sessions"
              value={
                overview.totalSessions
              }
              icon={
                <ClockIcon
                  className="w-5 h-5 text-[#1672b9]"
                />
              }
            />


            <MetricCard
              title="Avg Attendance"
              value={
                overview
                  .averageAttendance
                  .toFixed(1)
              }
              icon={
                <ChartBarIcon
                  className="w-5 h-5 text-[#1672b9]"
                />
              }
            />

          </div>


          {/* Charts */}

          <div className="
            grid
            grid-cols-1
            lg:grid-cols-2
            gap-6
          ">


            {/* Attendance Trend */}

            <ChartCard
              title="Daily Attendance Trend (Last 7 Days)"
            >

              <ResponsiveContainer
                width="100%"
                height={300}
              >

                <LineChart
                  data={attendanceTrend}
                >

                  <CartesianGrid
                    stroke="#e5e7eb"
                    strokeDasharray="3 3"
                    vertical={false}
                  />

                  <XAxis
                    dataKey="date"
                    tick={{
                      fill: '#6b7280',
                      fontSize: 12,
                    }}
                    axisLine={{
                      stroke: '#e5e7eb',
                    }}
                  />

                  <YAxis
                    tick={{
                      fill: '#6b7280',
                      fontSize: 12,
                    }}
                    axisLine={{
                      stroke: '#e5e7eb',
                    }}
                  />

                  <Tooltip
                    contentStyle={
                      tooltipStyle
                    }
                  />

                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#1672b9"
                    strokeWidth={2}
                    dot={{
                      fill: '#1672b9',
                    }}
                  />

                </LineChart>

              </ResponsiveContainer>

            </ChartCard>


            {/* Peak Hours */}

            <ChartCard
              title="Hourly Scan Distribution (Peak Hours)"
            >

              <ResponsiveContainer
                width="100%"
                height={300}
              >

                <BarChart
                  data={peakHours}
                >

                  <CartesianGrid
                    stroke="#e5e7eb"
                    strokeDasharray="3 3"
                    vertical={false}
                  />

                  <XAxis
                    dataKey="hour"
                    tick={{
                      fill: '#6b7280',
                      fontSize: 12,
                    }}
                    axisLine={{
                      stroke: '#e5e7eb',
                    }}
                  />

                  <YAxis
                    tick={{
                      fill: '#6b7280',
                      fontSize: 12,
                    }}
                    axisLine={{
                      stroke: '#e5e7eb',
                    }}
                  />

                  <Tooltip
                    contentStyle={
                      tooltipStyle
                    }
                  />

                  <Bar
                    dataKey="count"
                    fill="#1672b9"
                    radius={[
                      4,
                      4,
                      0,
                      0,
                    ]}
                  />

                </BarChart>

              </ResponsiveContainer>

            </ChartCard>

          </div>

        </div>
      )}


      {/* =====================================================
          EVENT ANALYTICS TAB
      ===================================================== */}

      {activeTab === 'event-analytics' && (

        <ChartCard
          title="Event-wise Comparison"
        >

          {eventComparison.length === 0 ? (

            <div className="py-16 text-center text-gray-500 text-sm">
              No event data available.
            </div>

          ) : (

            <ResponsiveContainer
              width="100%"
              height={400}
            >

              <BarChart
                data={eventComparison}
              >

                <CartesianGrid
                  stroke="#e5e7eb"
                  strokeDasharray="3 3"
                  vertical={false}
                />

                <XAxis
                  dataKey="name"
                  tick={{
                    fill: '#6b7280',
                    fontSize: 12,
                  }}
                  angle={-15}
                  textAnchor="end"
                  height={60}
                />

                <YAxis
                  tick={{
                    fill: '#6b7280',
                    fontSize: 12,
                  }}
                />

                <Tooltip
                  contentStyle={
                    tooltipStyle
                  }
                />

                <Legend
                  wrapperStyle={{
                    fontSize: 12,
                  }}
                />

                <Bar
                  dataKey="users"
                  fill="#1672b9"
                  name="Attendees"
                  radius={[
                    4,
                    4,
                    0,
                    0,
                  ]}
                />

                <Bar
                  dataKey="scans"
                  fill="#4895d4"
                  name="Total Scans"
                  radius={[
                    4,
                    4,
                    0,
                    0,
                  ]}
                />

              </BarChart>

            </ResponsiveContainer>

          )}

        </ChartCard>
      )}


      {/* =====================================================
          SESSION ANALYTICS TAB
      ===================================================== */}

      {activeTab === 'session-analytics' && (

        <div className="
          grid
          grid-cols-1
          sm:grid-cols-2
          lg:grid-cols-5
          gap-5
        ">

          <MetricCard
            title="Total Sessions"
            value={
              sessionAnalyticsData
                ?.totalSessions || 0
            }
            icon={
              <ClockIcon
                className="w-5 h-5 text-[#1672b9]"
              />
            }
          />


          <MetricCard
            title="Avg Duration (sec)"
            value={Number(
              sessionAnalyticsData
                ?.avgDuration || 0
            ).toFixed(1)}
            icon={
              <ClockIcon
                className="w-5 h-5 text-[#1672b9]"
              />
            }
          />


          <MetricCard
            title="Longest Session (sec)"
            value={
              sessionAnalyticsData
                ?.longest || 0
            }
            icon={
              <ArrowPathIcon
                className="w-5 h-5 text-[#1672b9]"
              />
            }
          />


          <MetricCard
            title="Shortest Session (sec)"
            value={
              sessionAnalyticsData
                ?.shortest || 0
            }
            icon={
              <ArrowPathIcon
                className="w-5 h-5 text-[#1672b9]"
              />
            }
          />


          <MetricCard
            title="Active Sessions (Live)"
            value={
              sessionAnalyticsData
                ?.activeSessions || 0
            }
            icon={
              <FireIcon
                className="w-5 h-5 text-[#1672b9]"
              />
            }
          />

        </div>
      )}


      {/* =====================================================
          DEVICE ANALYTICS TAB
      ===================================================== */}

      {activeTab === 'device-analytics' && (

        <div className="space-y-6">


          {/* Device Scan Counts */}

          <ChartCard
            title="Device Scan Counts"
          >

            {deviceAnalytics.length === 0 ? (

              <div className="py-16 text-center text-gray-500 text-sm">
                No device analytics available.
              </div>

            ) : (

              <ResponsiveContainer
                width="100%"
                height={400}
              >

                <BarChart
                  data={deviceAnalytics}
                >

                  <CartesianGrid
                    stroke="#e5e7eb"
                    strokeDasharray="3 3"
                    vertical={false}
                  />

                  <XAxis
                    dataKey="name"
                    tick={{
                      fill: '#6b7280',
                      fontSize: 12,
                    }}
                  />

                  <YAxis
                    tick={{
                      fill: '#6b7280',
                      fontSize: 12,
                    }}
                  />

                  <Tooltip
                    contentStyle={
                      tooltipStyle
                    }
                  />

                  <Bar
                    dataKey="scans"
                    fill="#1672b9"
                    name="Scans"
                    radius={[
                      4,
                      4,
                      0,
                      0,
                    ]}
                  />

                </BarChart>

              </ResponsiveContainer>

            )}

          </ChartCard>


          {/* Device Status */}

          <div className="
            bg-white
            rounded-xl
            border
            border-gray-100
            shadow-sm
            p-5
          ">

            <h3 className="
              text-base
              font-medium
              text-gray-900
              mb-4
            ">
              Device Status Overview
            </h3>


            {deviceAnalytics.length === 0 ? (

              <p className="text-sm text-gray-500">
                No device data available.
              </p>

            ) : (

              <div className="
                grid
                grid-cols-1
                sm:grid-cols-2
                lg:grid-cols-3
                gap-3
              ">

                {deviceAnalytics.map(
                  (device, index) => (

                    <div
                      key={
                        device.deviceId ||
                        device._id ||
                        device.name ||
                        index
                      }
                      className="
                        flex
                        items-center
                        justify-between
                        p-3
                        bg-gray-50
                        rounded-lg
                      "
                    >

                      <span className="
                        font-medium
                        text-gray-900
                        text-sm
                      ">
                        {device.name ||
                          device.deviceName ||
                          device.deviceId ||
                          'Unknown Device'}
                      </span>


                      <span
                        className={`
                          inline-flex
                          items-center
                          px-2.5
                          py-0.5
                          rounded-full
                          text-xs
                          font-medium

                          ${
                            device.status ===
                            'active'
                              ? 'bg-green-50 text-green-700'
                              : 'bg-gray-100 text-gray-500'
                          }
                        `}
                      >
                        {device.status ||
                          'unknown'}
                      </span>

                    </div>

                  )
                )}

              </div>

            )}

          </div>

        </div>
      )}

    </div>
  );
};


// =========================================================
// METRIC CARD
// =========================================================

const MetricCard = React.memo(
  ({
    title,
    value,
    icon,
    trend,
  }) => (

    <div className="
      bg-white
      rounded-xl
      border
      border-gray-100
      shadow-sm
      p-5
      transition-all
      hover:shadow-md
    ">

      <div className="
        flex
        items-center
        justify-between
      ">

        <div>

          <p className="
            text-xs
            font-medium
            text-gray-400
            uppercase
            tracking-wide
          ">
            {title}
          </p>


          <p className="
            text-2xl
            font-semibold
            text-gray-900
            mt-1
          ">
            {value}
          </p>


          {trend && (

            <p className="
              text-xs
              text-green-600
              mt-2
              flex
              items-center
              gap-1
            ">
              <span>↑</span>
              {trend}
            </p>

          )}

        </div>


        <div className="
          p-2
          bg-gray-50
          rounded-full
        ">
          {icon}
        </div>

      </div>

    </div>
  )
);

MetricCard.displayName =
  'MetricCard';


// =========================================================
// CHART CARD
// =========================================================

const ChartCard = React.memo(
  ({
    title,
    children,
  }) => (

    <div className="
      bg-white
      rounded-xl
      border
      border-gray-100
      shadow-sm
      p-5
    ">

      <h3 className="
        text-base
        font-medium
        text-gray-900
        mb-4
      ">
        {title}
      </h3>

      {children}

    </div>
  )
);

ChartCard.displayName =
  'ChartCard';


export default Dashboard;